import React, {Component} from 'react';
import {Button} from 'reactstrap';
import {withTranslation} from 'react-i18next';
import TradeFeeLevelForm from './TradeFeeLevelForm';
import TradeFeeUserForm from './TradeFeeUserForm';
import {NotificationManager} from '../components/common/react-notifications';
import {application, baseTokenSymbol, reactTableText} from '../constants/configs';
import ModalManager from '../components/modal/ModalManager';
import FoldableTableHOC from 'react-table/lib/hoc/foldableTable';
import {remove, zip, forEach, trim, find, sortBy, isEqual, findIndex} from 'lodash';
import LoaderButton from '../components/LoaderButton';
import {numberWithCommas} from '../helpers/Utils';
import ToolTip from '../components/ToolTip';
import Rbac from '../components/Rbac';
import ReactTable from 'react-table';
import Api from '../services/Api';

const FoldableTable = FoldableTableHOC(ReactTable);

class TradeFeePolicyLevels extends Component {
  constructor(props) {
    super(props);
    this.state = {
      policy: {...props.policy},
      submitting: false,
    };
  }

  deleteLevel = async level => {
    const {policy} = this.state;
    const {levels} = policy;
    const fiat = [...levels.fiat];
    const crypto = [...levels.crypto];
    this.setState({
      policy: {
        ...policy,
        levels: {
          fiat: remove(fiat, obj => obj.level !== level),
          crypto: remove(crypto, obj => obj.level !== level),
        },
      }
    });
  };

  deleteUser = async user => {
    const {policy} = this.state;
    const {levels} = policy;
    const fiat = [...levels.fiat];
    const crypto = [...levels.crypto];
    this.setState({
      policy: {
        ...policy,
        levels: {
          fiat: fiat.map(level => ({...level, fees: remove(level.fees, obj => obj.user !== user)})),
          crypto: crypto.map(level => ({...level, fees: remove(level.fees, obj => obj.user !== user)})),
        },
      }
    });
  };

  addUser = values => {
    const {policy} = this.state;
    const {levels} = policy;
    const {user} = values;
    this.setState({
      policy: {
        ...policy,
        levels: {
          fiat: levels.fiat.map(level => ({...level, fees: [...level.fees, {user: user.value, maker: '0', taker: '0'}]})),
          crypto: levels.crypto.map(level => ({...level, fees: [...level.fees, {user: user.value, maker: '0', taker: '0'}]})),
        },
      }
    });
  };

  editUser = (values, user) => {
    const {policy} = this.state;
    const {levels} = policy;
    this.setState({
      policy: {
        ...policy,
        levels: {
          fiat: levels.fiat.map(level => {
            let newFees = [...level.fees];
            let userIndex = findIndex(newFees, ['user', user]);
            let index = userIndex !== -1 ? userIndex : 0;
            newFees[index] = {
              ...newFees[index],
              user: values.user.value,
              ...(userIndex === -1 ? {maker: '0', taker: '0'} : null)
            };
            return {
              ...level,
              fees: newFees,
            };
          }),
          crypto: levels.crypto.map(level => {
            let newFees = [...level.fees];
            let userIndex = findIndex(newFees, ['user', user]);
            let index = userIndex !== -1 ? userIndex : 0;
            newFees[index] = {
              ...newFees[index],
              user: values.user.value,
              ...(userIndex === -1 ? {maker: '0', taker: '0'} : null)
            };
            return {
              ...level,
              fees: newFees,
            };
          }),
        },
      }
    });
  };

  addLevel = info => {
    const {policy} = this.state;
    const {fiatFees, cryptoFees, ...values} = info;
    this.setState({
      policy: {
        ...policy,
        levels: {
          fiat: [
            ...policy.levels.fiat,
            {
              ...values,
              fees: fiatFees,
            }
          ],
          crypto: [
            ...policy.levels.crypto,
            {
              ...values,
              fees: cryptoFees,
            }
          ],
        }
      }
    });
  };

  editLevel = (info, level) => {
    const {policy} = this.state;
    const {fiatFees, cryptoFees, ...values} = info;
    const fiat = [...policy.levels.fiat];
    const crypto = [...policy.levels.crypto];
    this.setState({
      policy: {
        ...policy,
        levels: {
          fiat: [
            ...remove(fiat, obj => obj.level !== level[0].level),
            {
              ...values,
              fees: fiatFees,
            }
          ].sort((a, b) => a.level - b.level),
          crypto: [
            ...remove(crypto, obj => obj.level !== level[0].level),
            {
              ...values,
              fees: cryptoFees,
            }
          ].sort((a, b) => a.level - b.level),
        },
      }
    });
  };

  onPressCreate = () => {
    const {t} = this.props;
    const {policy} = this.state;
    ModalManager.open({
      isOpen: true,
      title: t('Create Trade Fee Level'),
      body: <TradeFeeLevelForm
        policy={policy}
        addLevel={this.addLevel}
      />
    });
  };

  onPressEdit = level => {
    const {t} = this.props;
    const {policy} = this.state;
    ModalManager.open({
      isOpen: true,
      title: t('Edit Trade Fee Level'),
      body: <TradeFeeLevelForm
        level={level}
        policy={policy}
        editLevel={values => {this.editLevel(values, level);}}
      />
    });
  };

  onPressAddUser = () => {
    const {t} = this.props;
    const {policy} = this.state;
    ModalManager.open({
      isOpen: true,
      title: t('Add User'),
      body: <TradeFeeUserForm
        policy={policy}
        addUser={this.addUser}
      />
    });
  };

  onPressEditUser = user => {
    const {t} = this.props;
    const {policy} = this.state;
    ModalManager.open({
      isOpen: true,
      title: t('Edit User'),
      body: <TradeFeeUserForm
        user={user}
        policy={policy}
        editUser={values => {this.editUser(values, user);}}
      />
    });
  };

  onSubmit = async () => {
    const {policy} = this.state;
    const {t, fireFetchData} = this.props;
    this.setState({submitting: true});
    let newFiat = [...policy.levels.fiat];
    let newCrypto = [...policy.levels.crypto];
    newFiat[newFiat.length - 1] = {
      ...newFiat[newFiat.length - 1],
      max: null,
    };
    newCrypto[newCrypto.length - 1] = {
      ...newCrypto[newCrypto.length - 1],
      max: null,
    };
    try {
      await Api.invoke(
        'PATCH',
        '/admin-ui/loyalty-policies',
        {
          ...policy,
          levels: {
            fiat: newFiat,
            crypto: newCrypto,
          },
        },
      );
      NotificationManager.success(t('Loyalty policy levels updated successfully'), t('Message'));
      fireFetchData();
    } catch (e) {
      console.log('error: ', e);
    } finally {
      this.setState({submitting: false});
    }
  };

  getStaticColumns = t => [
    {
      Header: t('Level'),
      headerClassName: 'text-small',
      width: 40,
      Cell: props => <span>{props.original[0].level}</span>
    },
    {
      Header: `${t('30 Days Trades Vol')} (${t(baseTokenSymbol)})`,
      headerClassName: 'text-small',
      width: 155,
      Cell: props => <span>{props.original[0].max ? `${t('till')} ${numberWithCommas(props.original[0].max)}` : null}</span>
    },
    {
      Header: t('Actions'),
      headerClassName: 'text-small',
      width: 60,
      Cell: props => <div className='d-flex flex-row align-items-center justify-content-between px-2'>
        <span className="text-one text-primary" onClick={() => {this.onPressEdit(props.original);}}>
          <i className="simple-icon-pencil edit-icon cursor-pointer"/>
        </span>
        {props.index === this.state.policy.levels.fiat.length - 1 &&
          this.state.policy.levels.fiat.length > 1 &&
          <span
            className="text-one text-danger"
            onClick={() => {this.deleteLevel(props.original[0].level);}}
          >
            <i className="simple-icon-trash delete-icon cursor-pointer"/>
          </span>}
      </div>,
      show: Rbac.hasPermission('update_policies'),
    },
  ];

  getFoldableColumns = cols => {
    const {t} = this.props;
    const {policy} = this.state;
    const {fiat} = policy.levels;
    let foldableCols = [...cols];
    if (fiat[0].fees.length) {
      forEach(sortBy(fiat[0].fees, [obj => obj.user === `__${application.platformName}__`]), (value, index) => {
        let foldableCol = {
            foldable: true,
            Header:
              <div className='d-flex flex-row align-items-center justify-content-between'>
                <div>{t(`${trim(value.user, '_')} user fee`)}</div>
                <Rbac permission='update_policies'>
                  <div style={{width: index === 0 && fiat[0].fees.length !== 1 ? 90 : 60}} className='d-flex flex-row align-items-center justify-content-around'>
                  <span className="text-one text-primary" onClick={() => {this.onPressEditUser(value.user);}}>
                    <i className="simple-icon-pencil edit-icon cursor-pointer"/>
                  </span>
                  {index === 0 && fiat[0].fees.length !== 1 && <span className="text-one text-danger" onClick={() => {this.deleteUser(value.user);}}>
                    <i className="simple-icon-trash delete-icon cursor-pointer"/>
                  </span>}
                  {index === 0 && <ToolTip id={`add_user_${value.user}_${this.state.policy.id}`} text={t('Add New User')}>
                    <span className="text-one text-success" onClick={() => {this.onPressAddUser();}}>
                      <i className="simple-icon-plus add-reply-icon cursor-pointer"/>
                    </span>
                  </ToolTip>}
                </div>
                </Rbac>
              </div>,
            headerClassName: 'text-small',
            columns: [
              {
                Header: t('Fiat Maker'),
                headerClassName: 'text-small',
                Cell: props => {
                  const fee = find(props.original[0].fees, ['user', value.user]);
                  return (
                    <span>
                      {fee ? Number(find(props.original[0].fees, ['user', value.user]).maker) : null}
                    </span>
                  );
                },
              },
              {
                Header: t('Fiat Taker'),
                headerClassName: 'text-small',
                Cell: props => {
                  const fee = find(props.original[0].fees, ['user', value.user]);
                  return (
                    <span>
                      {fee ? Number(find(props.original[0].fees, ['user', value.user]).taker) : null}
                    </span>
                  );
                },
              },
              {
                Header: t('Crypto Maker'),
                headerClassName: 'text-small',
                Cell: props => {
                  const fee = find(props.original[1].fees, ['user', value.user]);
                  return (
                    <span>
                      {fee ? Number(find(props.original[1].fees, ['user', value.user]).maker) : null}
                    </span>
                  );
                },
              },
              {
                Header: t('Crypto Taker'),
                headerClassName: 'text-small',
                Cell: props => {
                  const fee = find(props.original[1].fees, ['user', value.user]);
                  return (
                    <span>
                      {fee ? Number(find(props.original[1].fees, ['user', value.user]).taker) : null}
                    </span>
                  );
                },
              },
            ]
          };
        foldableCols.splice(cols.length - 1, 0, foldableCol);
      });
    } else {
      let foldableCol = {
        foldable: true,
        Header: <div className='d-flex flex-row align-items-center justify-content-between'>
          <div>{t(`${application.platformName} user fee`)}</div>
          <Rbac permission='update_policies'>
            <div style={{width: 60}} className='d-flex flex-row align-items-center justify-content-around'>
              <span className="text-one text-primary" onClick={() => {this.onPressEditUser(`__${application.platformName}__`);}}>
                <i className="simple-icon-pencil edit-icon cursor-pointer"/>
              </span>
              <ToolTip id={`add_user_1_${this.state.policy.id}`} text={t('Add New User')}>
                <span className="text-one text-success" onClick={() => {this.onPressAddUser();}}>
                  <i className="simple-icon-plus add-reply-icon cursor-pointer"/>
                </span>
              </ToolTip>
            </div>
          </Rbac>
        </div>,
        headerClassName: 'text-small',
        columns: [
          {
            Header: t('Fiat Maker'),
            headerClassName: 'text-small',
            Cell: '0'
          },
          {
            Header: t('Fiat Taker'),
            headerClassName: 'text-small',
            Cell: '0'
          },
          {
            Header: t('Crypto Maker'),
            headerClassName: 'text-small',
            Cell: '0'
          },
          {
            Header: t('Crypto Taker'),
            headerClassName: 'text-small',
            Cell: '0'
          },
        ],
      };
      foldableCols.splice(cols.length - 1, 0, foldableCol);
    }
    return foldableCols;
  };

  render() {
    const {t} = this.props;
    const {policy, submitting} = this.state;
    const sortedFiat = policy.levels.fiat.sort((a, b) => a.level - b.level);
    const sortedCrypto = policy.levels.crypto.sort((a, b) => a.level - b.level);
    const levels = zip(sortedFiat, sortedCrypto);
    return (
      <div className='table-row-sub' style={{paddingRight: 10, paddingLeft: 10}}>
        <div className="text-one text-success d-flex flex-row justify-content-between align-items-center mt-4">
          {t('Trade Fee Levels')}
          <Rbac permission='update_policies'>
            <Button size='sm' onClick={this.onPressCreate}>
              <i className="simple-icon-plus add-reply-icon"/>
              <span className="mx-1">{t('Create Trade Fee Level')}</span>
            </Button>
          </Rbac>
        </div>
        <FoldableTable
          manual
          className="-highlight mt-2 mb-4"
          data={levels}
          sortable={false}
          resizable={true}
          showPagination={false}
          pageSize={policy.levels.fiat.length || 3}
          columns={this.getFoldableColumns(this.getStaticColumns(t))}
          style={{width: '100%'}}
          {...reactTableText}
        />
        <div className='mb-4 d-flex flex-row justify-content-end align-items-center'>
          <Rbac permission='update_policies'>
            <LoaderButton
              size='sm'
              color='success'
              style={{width: 140}}
              className='d-flex align-items-center justify-content-center'
              disabled={isEqual(policy, this.props.policy) || submitting}
              loading={submitting}
              onClick={this.onSubmit}>
              {t('Submit')}
            </LoaderButton>
          </Rbac>
        </div>
      </div>
    );
  }
}

export default withTranslation()(TradeFeePolicyLevels);
